import { TabData, TableData } from '@ddt-web/data-service';
import * as findIndex from 'lodash/findIndex';
import * as get from 'lodash/get';
import * as head from 'lodash/head';

import { ASSET_CLASSES } from '../constants/asset-classes.constants';
import { IScenarioAnalysisData, IScenarioChartData } from '../models';
import { CommonDataUtil } from './common-data.util';

/**
 * @description Util Class for Stacked Data types
 * @author aatray
 * @export
 * @class StackedDataUtil
 */
export class StackedDataUtil {
	/**
	 * @description Extracts Stacked Data in the format from TabData
	 * @author aatray
	 * @param {TabData} tabData
	 * @param {number} portfolioIndex allows for filtering of data by selected portfolio (index)
	 * @returns {ScenarioAnalysisData}
	 * @memberof ScenarioAnalysisUtil
	 */
	public static getStackedData(tabData: TabData, portfolioIndex?: number): IScenarioAnalysisData {
		const scenarioAnalysisData: IScenarioAnalysisData = {} as IScenarioAnalysisData;
		try {
			const tableData = head(tabData.tableData);
			scenarioAnalysisData.scenarios = tableData.headersX.filter(header => header.config.id === 'scenario').map(header => header.formattedValue);
			scenarioAnalysisData.series = [];
			scenarioAnalysisData.portfolios = [];

			let portfolioNames = tableData.headersY.map(header => header.formattedValue);
			// Fringe scenario: we have portfolios with identical names we'll need to ensure they are unique
			if (CommonDataUtil.hasDuplicateNames(portfolioNames)) {
				portfolioNames = CommonDataUtil.createUniqueNames(portfolioNames);
			}

			if (typeof portfolioIndex === 'number' && portfolioIndex > -1) {
				scenarioAnalysisData.portfolios.push(portfolioNames[portfolioIndex]);
			} else {
				scenarioAnalysisData.portfolios = portfolioNames;
			}

			if (typeof portfolioIndex === 'number' && portfolioIndex > -1) {
				Object.keys(ASSET_CLASSES).forEach(assetClass => {
					const scenarioChartData: IScenarioChartData = {} as IScenarioChartData;
					scenarioChartData.name = ASSET_CLASSES[assetClass];
					scenarioChartData.stack = scenarioAnalysisData.portfolios[0];
					scenarioChartData.data = this.getAssetClassValues(tableData, assetClass, scenarioAnalysisData.scenarios.length, portfolioIndex);
					scenarioAnalysisData.series.push(scenarioChartData);
				});
			} else {
				scenarioAnalysisData.portfolios.forEach((portfolio, index) => {
					Object.keys(ASSET_CLASSES).forEach(assetClass => {
						const scenarioChartData: IScenarioChartData = {} as IScenarioChartData;
						scenarioChartData.name = ASSET_CLASSES[assetClass];
						scenarioChartData.stack = portfolio;
						scenarioChartData.data = this.getAssetClassValues(tableData, assetClass, scenarioAnalysisData.scenarios.length, index);
						scenarioAnalysisData.series.push(scenarioChartData);
					});
				});
			}
		} catch (error) {
			console.error('Cannot get ScenarioAnalysis Data from TabData ', error);
		}
		return scenarioAnalysisData;
	}

	/**
	 * @description Extracts AssetClass values for each scenario from Tabledata and returns an array
	 *
	 * @author aatray
	 * @private
	 * @param {TableData} tableData
	 * @param {string} assetClass
	 * @param {number} noOfScenarios
	 * @param {number} [indexPosition=0]
	 * @returns {number[]}
	 * @memberof ScenarioAnalysisUtil
	 */
	private static getAssetClassValues(tableData: TableData, assetClass: string, noOfScenarios: number, indexPosition = 0): number[] {
		let assetClassValues = [];
		const assetClassIndices = [];
		try {
			let assetClassIndex = findIndex(tableData.headersX, header => header.rawValue === assetClass);
			if (assetClassIndex <= -1) {
				assetClassValues = new Array(noOfScenarios).fill(0);
			} else {
				if (assetClassIndex > -1) {
					assetClassIndices.push(assetClassIndex);
				}
				while (assetClassIndex > -1) {
					assetClassIndex = findIndex(tableData.headersX, header => header.rawValue === assetClass, ++assetClassIndex);
					if (assetClassIndex > -1) {
						assetClassIndices.push(assetClassIndex);
					}
				}
				assetClassIndices.forEach(index => {
					assetClassValues.push(get(tableData.cells[indexPosition][index], 'rawValue', 0));
				});
			}
		} catch (error) {
			console.error('Cannot get AssetClass Values from TabData ', error);
		}
		return assetClassValues;
	}


	/**
	 *
	 *
	 * @static
	 * @param {TabData} tabData the data.
	 * @param {number} portfolioIndex the selected portfolio index.
	 * @param {Array<string>} categories the categories which wish to plot.
	 * @param {string} scenario the scenario which we want to work with; this corresponds to the headerX config id.
	 * @returns {IScenarioAnalysisData} the scenario analysis data object - reflects stacked data types in general.
	 * @memberof StackedDataUtil
	 */
	public static getSplitColumnStackedData(tabData: TabData, portfolioIndex: number, categories: Array<string>, scenario: string): IScenarioAnalysisData {
		const scenarioAnalysisData: IScenarioAnalysisData = {} as IScenarioAnalysisData;
		scenarioAnalysisData.scenarios = [scenario];
		const headersY = get(tabData, 'tableData[0].headersY', []);
		scenarioAnalysisData.portfolios = headersY.map(header => header.formattedValue);
		scenarioAnalysisData.series = [];

		// loop through the headers finding the scenario that matches and category that we want to plot.
		const headersX = get(tabData, 'tableData[0].headersX', []);
		headersX.forEach((header, index) => {
			if (categories.indexOf(header.formattedValue) > -1 && get(header, 'config.id') === scenario) {
				const cellData = get(tabData, `tableData[0].cells[${portfolioIndex}][${index}]`, null);
				const scenario: IScenarioChartData = {} as IScenarioChartData;
				scenario.name = header.formattedValue;
				scenario.stack = header.config.id;
				scenario.data = cellData ? [cellData.rawValue as number] : [0];
				scenarioAnalysisData.series.push(scenario);

			}
		});
		return scenarioAnalysisData;
	}
}
